import React, { useState } from "react";
import { withAuthenticationRequired } from "@auth0/auth0-react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  Grid,
  Typography,
  InputAdornment,
  Card,
  CardContent,
  CardActions,
  Box,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import Loading from "../components/common/Loading";
import AppSnackbar from "../common/AppSnackbar";
import SearchIcon from "@mui/icons-material/Search";
import { useTranslation } from "react-i18next";
import { useReservationAdminAPI } from "../service/useReservationAdminAPI";
import { useNavigate } from "react-router-dom";

// Small helper for stacked label/value in cards
const InfoRow = ({ label, value }) => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
    <Typography variant="caption" sx={{ opacity: 0.8 }}>
      {label}
    </Typography>
    <Typography>{value || "-"}</Typography>
  </Box>
);

const Customers = () => {
  const { t } = useTranslation("");
  const st = (key, options) => t(`customersPage.${key}`, options);

  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));

  const [inputValue, setInputValue] = useState("");
  const { searchByCustomerName } = useReservationAdminAPI();
  const [personSearchResult, setPersonSearchResult] = useState([]);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [severity] = useState("error");
  const navigate = useNavigate();

  const handleAddOption = () => {
    if (inputValue) {
      searchByCustomerName(inputValue)
        .then((value) => setPersonSearchResult(value))
        .catch(() => {
          handleFetchError(t("searchFailure"));
        });
    }
  };

  function handleFetchError(reason) {
    setSnackbarMessage(reason);
    setOpenSnackbar(true);
  }

  const handleInputChange = (event) => {
    setInputValue(event.target.value);
  };

  return (
    <Grid
      container
      justifyContent="center"
      sx={{ px: { xs: 1, sm: 2 }, py: { xs: 1, sm: 2 } }}
    >
      <Grid item xs={12} sm={12} sx={{ p: 2, m: 2 }}>
        <Typography variant={isXs ? "h6" : "h5"} sx={{ mb: 2 }}>
          {st("title")}
        </Typography>

        <TextField
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleAddOption();
            }
          }}
          fullWidth={isXs}
          required
          variant="outlined"
          sx={{
            backgroundColor: "white",
            mb: 2,
            maxWidth: isXs ? "100%" : 420,
          }}
          value={inputValue}
          onChange={handleInputChange}
          label={st("searchLabel")}
          inputProps={{ "data-testid": "customer-search-input" }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Button
                  onClick={handleAddOption}
                  aria-label={st("searchLabel")}
                  data-testid="customer-search-button"
                >
                  <SearchIcon />
                </Button>
              </InputAdornment>
            ),
          }}
        />

        {/* Mobile: cards, Desktop: table */}
        {isXs ? (
          <Grid container spacing={1}>
            {personSearchResult.length > 0 ? (
              personSearchResult.map((person, idx) => (
                <Grid item xs={12} key={person.uuid || idx}>
                  <Card variant="outlined">
                    <CardContent sx={{ display: "grid", gap: 1 }}>
                      <InfoRow
                        label={st("name")}
                        value={`${person.forename} ${person.name}`}
                      />
                      <InfoRow
                        label={st("address")}
                        value={`${person.street} ${person.number}${person.bus ? " " + person.bus : ""}, ${person.postalcode} ${person.municipality}`}
                      />
                      <InfoRow label={st("email")} value={person.email} />
                      <InfoRow label={st("phone")} value={person.gsm} />
                    </CardContent>
                    <CardActions sx={{ justifyContent: "flex-end" }}>
                      <Button onClick={() => navigate(person.uuid)} data-testid={`view-customer-detail-button-${person.name}`}>
                        {st("viewDetail")}
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              ))
            ) : (
              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography color="text.secondary" align="center">
                      {st("noData")}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            )}
          </Grid>
        ) : (
          <TableContainer component={Paper}>
            <Table size="small" aria-label="customers table">
              <TableHead>
                <TableRow>
                  <TableCell>{st("name")}</TableCell>
                  <TableCell>{st("address")}</TableCell>
                  <TableCell>{st("email")}</TableCell>
                  <TableCell>{st("phone")}</TableCell>
                  <TableCell>{st("actions")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {personSearchResult.length > 0 ? (
                  personSearchResult.map((person, index) => (
                    <TableRow key={person.uuid || index}>
                      <TableCell>{`${person.forename} ${person.name}`}</TableCell>
                      <TableCell>{`${person.street} ${person.number}${person.bus ? " " + person.bus : ""} ${person.postalcode} ${person.municipality}`}</TableCell>
                      <TableCell>{person.email || "-"}</TableCell>
                      <TableCell>{person.gsm || "-"}</TableCell>
                      <TableCell>
                        <Button
                          onClick={() => navigate(person.uuid)}
                          color="primary"
                        >
                          {st("viewDetail")}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Typography color="text.secondary">
                        {st("noData")}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Grid>

      <AppSnackbar
        openSnackbar={openSnackbar}
        snackbarMessage={snackbarMessage}
        setOpenSnackbar={setOpenSnackbar}
        severity={severity}
      />
    </Grid>
  );
};

export default withAuthenticationRequired(Customers, {
  onRedirecting: () => <Loading />,
  returnTo: "/beheer/login",
});
